import { ADMIN_MESSAGE_FORMATS } from '../../../constants/messageFormats';
import { getSocket } from '../../../lib/socket';
import { formatDateJP, toDateInput, toJstEndOfDayOffset } from '../../../utils/datetime';
import { openExternalUrl } from '../../../utils/externalLink';
import { createEventAssetHelpers } from '../../common/chat/shared/eventAssetHelpers';
import { buildEventChatSearchUrl, createFetchJsonSafe } from '../../common/chat/eventlist/eventRequestUtils';

const API_URL = process.env.REACT_APP_API_URL;
const MESSAGE_IMAGE_PATH = process.env.REACT_APP_MESSAGE_IMAGE_PATH;
const MESSAGE_FILE_PATH = process.env.REACT_APP_MESSAGE_FILE_PATH;
const buildArtistEventNotesListUrl = (eventId, offset = 0, limit = 20) =>
  `${API_URL}/api/artist/event-notes/${eventId}?offset=${offset}&limit=${limit}`;
const FLYER_IMAGE_PATH = process.env.REACT_APP_FLYER_IMAGE_PATH;
const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH;
const ARTIST_PROFILE_IMAGE_PATH = process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH;
const EARLIEST_CREATED_AT = '1970-01-01T00:00:00Z'; // 新規スレッドでも新着取得できるようにする基準時刻
const STALE_MS = 60 * 60 * 1000; // 60分超はフルリロード
const UNSENDABLE_FORMATS = new Set([
  ADMIN_MESSAGE_FORMATS.TEXT,
  ADMIN_MESSAGE_FORMATS.IMAGE,
  ADMIN_MESSAGE_FORMATS.FILE,
]);

const DEBUG_RESTORE = true;
const dbg = (...args) => { if (DEBUG_RESTORE) console.log('[ArtistEventChat]', ...args); };

const ARTIST_EVENTS_HEAD_URL = `${API_URL}/api/artist/event-chat/available-events/head`;
const ARTIST_EVENTS_PAGE_URL = (offset) => `${API_URL}/api/artist/event-chat/available-events/${offset}`;
const ARTIST_SIDEBAR_ROW_URL = (eventId) => `${API_URL}/api/artist/event-chat/sidebar-row/${eventId}`;
const ARTIST_PARTICIPANTS_URL = (eventId) => `${API_URL}/api/artist/event-chat/participants/${eventId}`;
const ARTIST_GROUP_EVENTS_URL = (eventId) => `${API_URL}/api/artist/event-chat/group-events/${eventId}`;
const ARTIST_REPRESENTATIVE_EVENT_BY_THREAD_URL = (threadId) => `${API_URL}/api/artist/event-chat/representative-event/${threadId}`;
const EVENT_TARGET_ROW_CLASSES = {
  root: 'event-target-row chat-target-row chat-target-row--event',
  left: 'event-target-row__media chat-target-row__media event-list__flyer-shell',
  center: 'event-target-row__text chat-target-row__text',
  nameLine: 'event-target-row__title-line chat-target-row__title-line',
  name: 'event-list__title',
  preview: 'event-list__last-preview',
  below: 'event-target-row__notice chat-target-row__below',
  right: 'event-target-row__side chat-target-row__side',
  time: 'event-list__time',
  badge: 'event-list__unread-badge',
};
const ARTIST_EVENT_SEARCH_PAGE_LIMIT = 50;

const ARTIST_SEARCH_API = ({
  q = '',
  message = '',
  startDate = '',
  endDate = '',
  offset = 0,
  limit = ARTIST_EVENT_SEARCH_PAGE_LIMIT,
  snapshotToken = '',
} = {}) => {
  return buildEventChatSearchUrl({
    apiUrl: API_URL,
    role: 'artist',
    q,
    message,
    startDate,
    endDate,
    offset,
    limit,
    snapshotToken,
  });
};

const {
  buildAssetUrl,
  buildFlyerUrl,
  buildMessageImageUrl,
  applyLandscapeContain,
} = createEventAssetHelpers({
  apiUrl: API_URL,
  flyerImagePath: FLYER_IMAGE_PATH,
  messageImagePath: MESSAGE_IMAGE_PATH,
});

const formatExpireDateLabel = (value) => {
  const ymd = toDateInput(value);
  return ymd ? formatDateJP(ymd) : '';
};

const socket = getSocket();
const fetchJsonSafe = createFetchJsonSafe({
  useStatusMessage: true,
  networkErrorMessage: 'ネットワークに接続できません。通信環境をご確認ください。',
});

const MS_DAY = 24 * 60 * 60 * 1000;
const endOfDayJst = (yyyy_mm_dd) => {
  const s = toJstEndOfDayOffset(yyyy_mm_dd);
  return s ? new Date(s) : null;
};
const handleDownload = (type, id, fallbackUrl) => {
  try {
    if (id) {
      const url = `${API_URL}/api/event-chat/${type === 'image' ? 'images' : 'files'}/${id}/download`;
      openExternalUrl(url);
    } else if (fallbackUrl) {
      const a = document.createElement('a');
      a.href = fallbackUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    }
  } catch { /* no-op */ }
};
const ACCESS_DENIED_STATUSES = new Set([401, 403, 404]);
const MAX_FILE_BYTES = 100 * 1024 * 1024;
const MAX_IMAGE_COUNT = 5;

export {
  API_URL,
  MESSAGE_FILE_PATH,
  buildArtistEventNotesListUrl,
  ADMIN_PROFILE_IMAGE_PATH,
  ARTIST_PROFILE_IMAGE_PATH,
  EARLIEST_CREATED_AT,
  STALE_MS,
  UNSENDABLE_FORMATS,
  dbg,
  ARTIST_EVENTS_HEAD_URL,
  ARTIST_EVENTS_PAGE_URL,
  ARTIST_SIDEBAR_ROW_URL,
  ARTIST_PARTICIPANTS_URL,
  ARTIST_GROUP_EVENTS_URL,
  ARTIST_REPRESENTATIVE_EVENT_BY_THREAD_URL,
  EVENT_TARGET_ROW_CLASSES,
  ARTIST_SEARCH_API,
  buildAssetUrl,
  buildFlyerUrl,
  buildMessageImageUrl,
  applyLandscapeContain,
  formatExpireDateLabel,
  socket,
  fetchJsonSafe,
  MS_DAY,
  endOfDayJst,
  handleDownload,
  ACCESS_DENIED_STATUSES,
  MAX_FILE_BYTES,
  MAX_IMAGE_COUNT,
};
